import { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, X, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { useChat, fetchServerSentEvents, type UIMessage } from '@tanstack/ai-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { tutorApi, type ChatTurn } from '@/lib/api/tutor';
import { useAuthStore } from '@/stores/authStore';

// Assistant replies are Markdown (Nudge's prompt asks for occasional bold /
// bullets), so they need real rendering — a bare <p> showed literal "**"
// and "-" characters to the student. Sized down from ProblemDescription's
// prose scale to fit a narrow chat bubble rather than a full reading pane.
const assistantProseCls =
  'prose prose-sm max-w-none leading-relaxed dark:prose-invert ' +
  'prose-p:my-1.5 prose-p:leading-relaxed prose-strong:font-semibold ' +
  'prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0 ' +
  'prose-code:rounded prose-code:bg-background/60 prose-code:px-1 prose-code:py-0.5 ' +
  'prose-code:font-mono prose-code:text-[0.85em] prose-code:font-normal ' +
  'prose-code:before:content-none prose-code:after:content-none ' +
  'prose-pre:my-1.5 prose-pre:bg-background/60';

type Props = {
  slug: string;
  code: string;
  onClose: () => void;
  /**
   * Mirrors LeetCode's own "Ask Leet" panel: minimizing collapses it to a
   * slim vertical rail (chat state stays warm underneath, same PanelGroup
   * pane, just shrunk) rather than unmounting it — a distinct action from
   * `onClose`, which fully removes the panel. Optional so this component
   * still works standalone (e.g. in tests) without a collapsible parent.
   */
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
};

// In production the platform (Cloudflare Pages) and the API (api.kodprep.com)
// are on different origins, so a relative URL here would resolve against the
// platform's own origin and get the SPA's index.html back instead of an SSE
// stream. Every other fetch call in this app prefixes VITE_API_URL for this
// exact reason (see src/main.tsx, src/features/Auth/OAuthPopup.ts,
// src/features/Profile/Avatar.tsx) — local dev works without it only because
// vite's dev-server proxy masks the missing prefix.
const API_BASE = import.meta.env.VITE_API_URL || '';

const headerCls =
  'flex shrink-0 items-center justify-between border-b border-border bg-muted/30 px-3 py-2';

/**
 * "Nudge" — KodPrep's in-workspace AI tutor chat, styled and interactively
 * modeled on LeetCode's own "Ask Leet" panel (confirmed live against
 * leetcode.com/problems/two-sum): a header with a label and close button, a
 * scrollable message list with visually distinct user/assistant turns, and a
 * composer whose Enter-to-send / Shift+Enter-for-newline behavior and hint
 * copy ("⇧⏎ to insert a line break.") reproduce LeetCode's own composer.
 *
 * Deliberately not replicated: the model picker, the "Agent" toggle, and the
 * paywall banner — this feature has a single server-configured model, no
 * agent mode, and no usage cap UI.
 *
 * This outer component owns the history fetch and gates mounting the actual
 * chat body (`AskLeetsChatBody`, where `useChat` lives) on that fetch
 * resolving. `useChat`'s `initialMessages` — and the `ChatClient` it
 * constructs internally — are only ever consumed on the hook's very first
 * render, so if `useChat` mounted immediately with an in-flight history
 * query, it would permanently start with an empty transcript: the query
 * would resolve later, but nothing would feed that result back into
 * `useChat`'s already-initialized state. Waiting here — rather than
 * conditionally skipping `useChat` inside one component — is also what the
 * rules of hooks require: hooks must run in the same order on every render,
 * so "call `useChat` only once data is ready" has to be a mount/unmount
 * decision made by a parent, not a conditional call in the same component.
 */
export function AskLeetsPanel({ slug, code, onClose, isCollapsed, onToggleCollapse }: Props) {
  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['tutor-history', slug],
    queryFn: () => tutorApi.getHistory(slug),
  });

  if (isCollapsed) {
    return <CollapsedRail onExpand={onToggleCollapse} />;
  }

  if (historyLoading) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <PanelHeader onClose={onClose} isCollapsed={isCollapsed} onToggleCollapse={onToggleCollapse} />
        <div className="flex-1 overflow-y-auto px-3 py-3">
          <p className="text-xs text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <AskLeetsChatBody
      slug={slug}
      code={code}
      onClose={onClose}
      history={history ?? []}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
    />
  );
}

/**
 * The collapsed state: a slim vertical rail with a rotated label, matching
 * LeetCode's own minimize behavior for "Ask Leet" — clicking anywhere on it
 * re-expands. The chat itself stays mounted in the parent Panel (just
 * shrunk to `collapsedSize`), so nothing here needs to know about messages.
 */
function CollapsedRail({ onExpand }: { onExpand?: () => void }) {
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label="Expand Nudge"
      className="flex h-full w-full flex-col items-center justify-start gap-3 py-3 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
    >
      <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
      <span
        className="text-sm font-medium tracking-wide"
        style={{ writingMode: 'vertical-rl' }}
      >
        Nudge
      </span>
    </button>
  );
}

/**
 * Three staggered bouncing dots inside the assistant's placeholder bubble —
 * the standard "thinking" affordance in chat UIs (ChatGPT, Slack, LeetCode's
 * own Ask Leet), used here instead of a bare empty bubble.
 */
function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-0.5" aria-label="Nudge is thinking">
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60" />
    </div>
  );
}

function PanelHeader({
  onClose,
  isCollapsed,
  onToggleCollapse,
}: {
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  return (
    <div className={headerCls}>
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <span className="text-sm font-medium">Nudge</span>
      </div>
      <div className="flex items-center gap-0.5">
        {onToggleCollapse && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onToggleCollapse}
            title="Minimize"
            aria-label="Minimize"
          >
            {isCollapsed ? (
              <PanelRightOpen className="h-3.5 w-3.5" />
            ) : (
              <PanelRightClose className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/**
 * The actual chat surface. Split out from `AskLeetsPanel` so `useChat` only
 * ever mounts once `history` has already resolved — see the parent's doc
 * comment for why that ordering matters.
 */
function AskLeetsChatBody({
  slug,
  code,
  onClose,
  history,
  isCollapsed,
  onToggleCollapse,
}: {
  slug: string;
  code: string;
  onClose: () => void;
  history: ChatTurn[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  // The current code is read fresh on every send via this ref rather than as
  // a `useChat` dependency — the connection object passed to `useChat` is
  // only ever read once (at mount), so closing over a ref here is what lets
  // each request carry the code as it stands *at send time* without tearing
  // down and recreating the chat client (and losing in-memory history) on
  // every keystroke in the editor.
  const codeRef = useRef(code);
  codeRef.current = code;

  const initialMessages: Array<UIMessage> = useMemo(
    () =>
      history.map((turn, i) => ({
        id: `history-${i}`,
        role: turn.role,
        parts: [{ type: 'text' as const, content: turn.content }],
      })),
    [history],
  );

  // fetchServerSentEvents issues a native fetch(), which does NOT go through
  // apiClient's axios interceptor — so the Bearer token has to be attached
  // here explicitly, read fresh from the auth store on every send (same
  // reasoning as codeRef above: a stale token captured once at mount would
  // break the request after any token refresh).
  const connection = useMemo(
    () =>
      fetchServerSentEvents(`${API_BASE}/api/v1/tutor/problems/${slug}/chat`, () => ({
        body: { code: codeRef.current },
        headers: {
          Authorization: `Bearer ${useAuthStore.getState().accessToken}`,
        },
      })),
    [slug],
  );

  const { messages, sendMessage, isLoading, error, reload } = useChat({ connection, initialMessages });

  const queryClient = useQueryClient();
  const wasLoadingRef = useRef(false);
  useEffect(() => {
    // Invalidate on the true -> false transition (a stream just finished),
    // not on mount (isLoading starts false). Without this, closing and
    // reopening the panel within the same session relies on React Query's
    // default refetch timing and can show stale history missing the turn
    // that just streamed.
    if (wasLoadingRef.current && !isLoading) {
      queryClient.invalidateQueries({ queryKey: ['tutor-history', slug] });
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, queryClient, slug]);

  const handleSend = () => {
    const content = draft.trim();
    if (!content || isLoading) return;
    sendMessage(content);
    setDraft('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, isLoading]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PanelHeader onClose={onClose} isCollapsed={isCollapsed} onToggleCollapse={onToggleCollapse} />

      <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {messages.map((message, i) => {
          const text = message.parts
            .map((part) => (part.type === 'text' ? part.content : ''))
            .join('');
          // useChat pushes the assistant's placeholder message the instant a
          // turn starts, with no text part yet — before this had a dedicated
          // indicator, that placeholder rendered as a bare empty bubble for
          // however long the first token took to arrive (or forever, on a
          // stream that errors before producing any content).
          const isPendingAssistantTurn =
            message.role === 'assistant' && text.length === 0 && isLoading && i === messages.length - 1;

          return (
            <div key={message.id} className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div
                className={
                  message.role === 'user'
                    ? 'max-w-[85%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground'
                    : 'max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm'
                }
              >
                {isPendingAssistantTurn ? (
                  <TypingDots />
                ) : message.role === 'assistant' ? (
                  <div className={assistantProseCls}>
                    <Markdown remarkPlugins={[remarkGfm]}>{text}</Markdown>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{text}</p>
                )}
              </div>
            </div>
          );
        })}

        {/*
         * The connection's own request can fail outright — a bad upstream
         * status from the LLM provider, a dropped connection — before it
         * ever streams a token. useChat surfaces that as `error` rather
         * than a message; left unhandled, the turn just vanishes with no
         * placeholder, no error, nothing indicating anything happened.
         */}
        {error && !isLoading && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <p>Nudge couldn't respond just now. Want to try again?</p>
              <Button variant="outline" size="sm" className="mt-2 h-7" onClick={() => reload()}>
                Try again
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-border p-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Nudge for a hint..."
          className="min-h-[64px] resize-none text-sm"
          rows={2}
        />
        <div className="mt-1.5 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">⇧⏎ to insert a line break.</span>
          <Button size="sm" onClick={handleSend} disabled={!draft.trim() || isLoading} aria-label="Send">
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
